import { CardWrapper } from 'admin/components/wrappers';
import CustomDataGrid from 'app/components/CustomDataGrid/Index';
import styled from 'styled-components';
import { useMemo, useState } from 'react';
import { GridPageChangeEvent } from '@progress/kendo-react-grid';
import { GeneralLookup, RegistrationReportSummary } from 'services/openapi';
import AdminDropDown from 'app/components/AdminDropDown';
import { RecordManualPaymentRequest } from 'services/openapi/models/RecordManualPaymentRequest';
import { useRegistrationReport } from 'admin/hooks/useRegistrationReport';
import AdminCustomButton from 'app/components/AdminCustomButton';

type Props = {
  registrationReportData?: RegistrationReportSummary;
  pageNumber: number;
  pageSize: number;
  onPageChange: (event: GridPageChangeEvent) => void;
  type: string;
  fromDate: string;
  toDate: string;
  searchName: string;
  pageSizeSelected: GeneralLookup;
  transformedData: GeneralLookup[];
  fetchRegistrationReport: (filterType?: string, filterFromDate?: string, filterToDate?: string, filterSearchName?: string, filterPageNumber?: number, filterPageSize?: number) => void;
  isPaymentLoading: boolean
};

const RegistrationReportTable = ({
  registrationReportData,
  pageNumber,
  pageSize,
  onPageChange,
  pageSizeSelected,
  transformedData,
  type,
  fetchRegistrationReport, isPaymentLoading
}: Props) => {
  const [selectedPaymentPlans, setSelectedPaymentPlans] = useState<{ [key: string]: GeneralLookup }>({});
  const [loadingPaymentId, setLoadingPaymentId] = useState<string | null>(null);
  const { recordManualPayment } = useRegistrationReport();

  const PaymentDateCell = (props: any) => {
    const date = props.dataItem?.paymentDate;
    if (!date) return <span>-</span>;
    const formattedDate = new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
    return (
      <td>
        <span>{formattedDate}</span>
      </td>
    );
  };
  const PaymentCell = (props: any) => {
    const amount = props.dataItem?.amountPaid;
    if (amount === null || amount === undefined) return <span>-</span>;
    return (
      <td>
        <span>${amount.toFixed(2)}</span>
      </td>
    );
  };
  const updateAmountCell = (props: any) => {
    const amount = props.dataItem?.amountPaid;
    if (amount === null || amount === undefined || amount !== 0) return <td> <span>-</span></td>;

    const seasonMapId = props.dataItem?.seasonMapId;
    const rowType = props.dataItem?.type;
    const selectedPlan = selectedPaymentPlans[seasonMapId];

    const handleDropdownChange = (selectedPlanData: GeneralLookup) => {
      const recordPayment: RecordManualPaymentRequest = {
        seasonMapId: seasonMapId || '',
        type: rowType || '',
        paymentPlanId: selectedPlanData.id,
      };
      setSelectedPaymentPlans(prev => ({
        ...prev,
        [seasonMapId]: selectedPlanData,
      }));
      console.log('Payment Record:', recordPayment);
    };

    const handleSavePayment = async () => {
      if (!selectedPlan) {
        return;
      }

      const paymentData: RecordManualPaymentRequest = {
        seasonMapId: seasonMapId || '',
        type: rowType || '',
        paymentPlanId: selectedPlan.id,
      };

      setLoadingPaymentId(seasonMapId);
      try {
        await recordManualPayment(paymentData);
        // Clear the selection after successful payment
        setSelectedPaymentPlans(prev => {
          const updated = { ...prev };
          delete updated[seasonMapId];
          return updated;
        });
        // Refresh the registration report data
        fetchRegistrationReport();
      } catch (error) {
        console.error('Payment recording failed:', error);
      } finally {
        setLoadingPaymentId(null);
      }
    };

    const handleCancelPayment = () => {
      // Clear the selection
      setSelectedPaymentPlans(prev => {
        const updated = { ...prev };
        delete updated[seasonMapId];
        return updated;
      });
    };

    return (
      <td>
        <UpdateAmountWrapper>
          <AdminDropDown
            data={transformedData}
            onDataChange={handleDropdownChange}
            defaultValue={selectedPlan}
            disabled={isPaymentLoading}
          />
          {selectedPlan && (
            <IconWrapper>
              <AdminCustomButton
                buttonType="cancel"
                onClick={handleCancelPayment}
                disabled={isPaymentLoading}
              >
                Cancel
              </AdminCustomButton>
              <AdminCustomButton
                buttonType="save"
                onClick={handleSavePayment}
                disabled={isPaymentLoading}
              >
                {isPaymentLoading ? 'Saving...' : 'Save'}
              </AdminCustomButton>
            </IconWrapper>
          )}
        </UpdateAmountWrapper>
      </td>
    );
  };
  const RiderColumn = useMemo(
    () => {
      // Check if any payment plan is selected
      const hasSelectedPlan = Object.keys(selectedPaymentPlans).length > 0;
      const updateAmountWidth = hasSelectedPlan ? '350' : '200';

      return [
        {
          field: 'name',
          title: 'Name',
          width: '280',
          locked: true,
          sortable: true,
        },
        {
          field: 'type',
          title: 'Type',
          width: '150',
          sortable: true,
        },
        {
          field: 'paymentDate',
          title: 'Payment Date',
          width: '220',
          sortable: true,
          cell: PaymentDateCell,
        },
        {
          field: 'amountPaid',
          title: 'Amount Paid',
          width: '180',
          sortable: true,
          cell: PaymentCell,
        },
        {
          field: 'updateAmount',
          title: 'Update Amount',
          width: updateAmountWidth,
          sortable: true,
          cell: updateAmountCell,
        },
      ];
    },
    [selectedPaymentPlans, transformedData]);

  return (
    <CardWrapper style={{ paddingTop: '20px', paddingBottom: '20px' }}>
      <GridWrapper>
        <CustomDataGrid
          showPagination={(registrationReportData?.totalCount ?? 0) > Number(pageSizeSelected.id)}
          data={registrationReportData?.items || []}
          columns={RiderColumn}
          pageSize={pageSize}
          totalCount={registrationReportData?.totalCount || 0}
          onPageChange={onPageChange}
          skip={(pageNumber - 1) * pageSize}
        />
      </GridWrapper>
    </CardWrapper>
  );
};

export default RegistrationReportTable;

const GridWrapper = styled.div`
  width: 100%;
  .k-grid-container .k-grid-content tr td {
    padding: 15px !important;
  }
  .k-grid-container {
    height: auto;
    min-height: auto;
  }

  .k-grid {
    border: 1px solid #e0e0e0;
    border-radius: 4px;
  }

  .k-grid-header .k-table,
  .k-grid-content .k-table {
    min-width: 1380px;
  }

  .k-grid-content {
    .k-grid-table {
      min-width: 1380px;
    }
  }

  .k-grid .flag-row .flag-cell {
    background-color: #e4e1c3 !important;
    color: #e91515 !important;
    font-weight: 500;
    padding: 8px 12px;
  }

  .k-grid-content tr td.team-cell {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 200px;
  }

  .k-grid-content tr .rider-name-style {
    .name-label,
    .sub-name-label {
      cursor: pointer !important;
    }
  }

  .k-table-thead {
    .k-link {
      cursor: default;
    }
    .sif-join-header-style,
    .team-header-style,
    .rider-header-style {
      cursor: pointer !important;
      .k-link {
        cursor: pointer !important;
      }
    }
  }

  .k-pager-wrap {
    padding: 15px 20px;
    border-top: 1px solid #e0e0e0;
  }
`;
const UpdateAmountWrapper = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  width: auto;
  height: 100%;
  max-width: 100%;
  
  .SHdropdown {
    flex: 0 1 auto;
    min-width: 200px;
  }
`;

const IconWrapper = styled.div`
  display: flex;
  gap: 6px;
  align-items: center;
  flex-shrink: 0;
  white-space: nowrap;

  button:disabled {
    pointer-events: none;
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const BaseIcon = styled.button`
  background: none;
  border: 1px solid #d0d0d0;
  font-size: 14px;
  cursor: pointer;
  padding: 6px 12px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  font-weight: 500;
  min-width: 60px;
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
